import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/shared/StatusBadge';
import StaffLink from '../../components/shared/StaffLink';
import WalletBalanceCard from '../../components/WalletBalanceCard';
import {
  UserGroupIcon,
  ShieldCheckIcon,
  EyeIcon,
  ChatBubbleLeftRightIcon,
  ExclamationTriangleIcon,
  ListBulletIcon,
  ClockIcon,
  ArrowRightOnRectangleIcon,
  ArrowLeftOnRectangleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
// expiry_date is a DATE column — never a JS Date (census #194). See lib/dateOnly.js.
import { formatDateOnlyNumeric } from '../../lib/dateOnly';

const ROLE_LABEL = {
  director: 'Director',
  admin: 'Office Admin',
  teacher: 'Teacher',
  float: 'Float Teacher',
  cook: 'Cook',
};

function formatElapsed(startISO) {
  const ms = Date.now() - new Date(startISO).getTime();
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

// Staff-facing homepage. Replaces the bare 4-quick-link Welcome that
// non-directors used to land on. v1 focuses on identity + clock-in/out:
// the daily anchor everyone needs. Schedule strip + status cards land
// in follow-up PRs.
function StaffHomepage({ user }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [, setTick] = useState(0); // forces a re-render every second when clocked in

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/me/clock-status');
      setStatus(data);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load clock status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Tick once a second so the elapsed timer updates while clocked in.
  useEffect(() => {
    if (!status?.clocked_in) return;
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [status?.clocked_in]);

  const doClockIn = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/me/clock-in', {});
      if (data.late_arrival) {
        toast(`Clocked in. ${data.late_arrival.minutes_late} min late — 0.5 attendance point added.`, { duration: 6000 });
      } else if (data.late_return_from_break) {
        toast(`Back from lunch. Over 65 min — 0.5 attendance point added.`, { duration: 6000 });
      } else {
        toast.success('Clocked in.');
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Clock-in failed.');
    } finally { setBusy(false); }
  };

  const doClockOut = async () => {
    setBusy(true);
    try {
      const { data } = await api.post('/me/clock-out', {});
      toast.success(`Clocked out. ${Math.floor(data.minutes_worked / 60)}h ${data.minutes_worked % 60}m on the clock.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Clock-out failed.');
    } finally { setBusy(false); }
  };

  const greeting = greetingFor(new Date());
  const roleLabel = ROLE_LABEL[user?.role] || user?.role || 'Team member';
  const todayLabel = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const clockedIn = !!status?.clocked_in;
  const elapsed = clockedIn ? formatElapsed(status.current_shift_started_at) : null;
  const todayShift = status?.today_shift; // {start_time, end_time, classroom} if backend returns it

  return (
    <div className="animate-slide-up max-w-3xl mx-auto space-y-6">
      {/* ─── Header ──────────────────────────────────────────── */}
      <div>
        <p className="text-sm text-gray-500">{todayLabel}</p>
        <h1 className="text-3xl font-bold text-gray-900 mt-1">
          {greeting}, {user?.first_name}
        </h1>
        <p className="text-gray-600 mt-1">
          {roleLabel}
          {todayShift?.classroom && (
            <> · today in <span className="font-medium text-gray-800">{todayShift.classroom}</span></>
          )}
        </p>
      </div>

      {/* ─── Points Wallet — front-and-center on the staff home ─
          Balance, cashout CTA, and quick history. */}
      <WalletBalanceCard userId={user?.id} />

      {/* ─── Assessment prompt ────────────────────────────────
          Shows for anyone with survey_completed_at = NULL. Hides itself
          the moment they submit. Front-and-center so staff don't have
          to know about the sidebar entry. */}
      {status?.assessment_pending && (
        <Link to="/my/assessment"
          className="block rounded-2xl border border-orange-300 bg-gradient-to-br from-orange-50 to-amber-50 p-5 hover:border-orange-400 transition-colors">
          <div className="flex items-start gap-3">
            <div className="bg-orange-500 rounded-xl p-2 flex-shrink-0">
              <SparklesIcon className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-900">Take your Talent &amp; Culture Assessment</p>
              <p className="text-sm text-gray-600 mt-0.5">
                Quick — about 8–10 minutes. Helps us understand what you bring to the team and how to support you.
              </p>
              <p className="text-sm font-medium text-orange-600 mt-2">Start the assessment →</p>
            </div>
          </div>
        </Link>
      )}

      {/* ─── Clock In / Out card ─────────────────────────────── */}
      <div className={`rounded-2xl p-6 border ${
        clockedIn
          ? 'bg-green-50 border-green-200'
          : 'bg-orange-50 border-orange-200'
      }`}>
        {loading ? (
          <div className="h-32 flex items-center justify-center text-gray-400">Loading…</div>
        ) : clockedIn ? (
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 text-green-700 text-sm font-medium mb-1">
                <ClockIcon className="w-4 h-4" /> You're clocked in
              </div>
              <p className="text-4xl font-bold text-gray-900 tabular-nums">{elapsed}</p>
              {status?.current_shift_started_at && (
                <p className="text-xs text-gray-600 mt-1">
                  Since {new Date(status.current_shift_started_at).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: 'numeric', minute: '2-digit' })}
                </p>
              )}
            </div>
            <button
              onClick={doClockOut}
              disabled={busy}
              className="w-full bg-gray-900 hover:bg-gray-800 disabled:bg-gray-900/50 text-white text-lg font-semibold py-4 rounded-xl inline-flex items-center justify-center gap-2"
            >
              <ArrowLeftOnRectangleIcon className="w-5 h-5" />
              {busy ? 'Clocking out…' : 'Clock out'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 text-orange-700 text-sm font-medium mb-1">
                <ClockIcon className="w-4 h-4" /> Ready to start your day
              </div>
              <p className="text-gray-700">
                {todayShift?.start_time
                  ? <>Your shift starts at <strong>{todayShift.start_time}</strong>.</>
                  : "Tap below when you're at the center and ready to work."}
              </p>
            </div>
            <button
              onClick={doClockIn}
              disabled={busy}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 text-white text-lg font-semibold py-4 rounded-xl inline-flex items-center justify-center gap-2"
            >
              <ArrowRightOnRectangleIcon className="w-5 h-5" />
              {busy ? 'Clocking in…' : 'Clock in'}
            </button>
            <p className="text-center text-xs text-gray-500">
              At a kiosk? Scan the QR code on the tablet — it'll clock you in automatically.
            </p>
          </div>
        )}
      </div>

      {/* ─── Quick links to existing pages ───────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <CompactLink to="/compliance" icon={ShieldCheckIcon} label="Compliance" />
        <CompactLink to="/observations" icon={EyeIcon} label="Observations" />
        <CompactLink to="/one-on-ones" icon={ChatBubbleLeftRightIcon} label="1:1s" />
        <CompactLink to="/tasks" icon={ListBulletIcon} label="Tasks" />
      </div>
    </div>
  );
}

function greetingFor(d) {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

function CompactLink({ to, icon: Icon, label }) {
  return (
    <Link to={to}
      className="bg-white rounded-xl border border-gray-200 px-4 py-3 hover:border-orange-300 hover:shadow-sm transition-all flex items-center gap-2.5">
      <Icon className="w-4 h-4 text-orange-500 flex-shrink-0" />
      <span className="text-sm font-medium text-gray-800">{label}</span>
    </Link>
  );
}

export default function DashboardPage() {
  const { user, isDirector } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      if (isDirector) {
        const { data } = await api.get('/dashboard');
        setDashboard(data);
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
      </div>
    );
  }

  // Non-director: staff homepage with clock in/out front and center.
  if (!isDirector) {
    return <StaffHomepage user={user} />;
  }

  // Director dashboard
  const staffCounts = dashboard?.staff_counts || [];
  const totalStaff = staffCounts.reduce((sum, s) => sum + parseInt(s.count), 0);
  const activeStaff = staffCounts.find(s => s.status === 'active')?.count || 0;

  return (
    <div className="animate-slide-up">
      <h1 className="text-2xl font-bold text-gray-900 mb-1">Director Dashboard</h1>
      <p className="text-gray-500 mb-6">Center-wide overview and accountability metrics.</p>

      {/* Summary Cards — each links to the relevant detail page */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <SummaryCard
          label="Active Staff"
          value={activeStaff}
          sub={`${totalStaff} total`}
          color="blue"
          to="/staff"
        />
        <SummaryCard
          label="Compliance Alerts"
          value={(dashboard?.compliance_alerts || []).length}
          sub="expiring / expired"
          color="red"
          to="/compliance?status=alerts"
        />
        <SummaryCard
          label="Overdue Tasks"
          value={(dashboard?.overdue_tasks || []).length}
          sub="need attention"
          color="amber"
          to="/tasks"
        />
        <SummaryCard
          label="Active Candidates"
          value={(dashboard?.active_candidates || []).reduce((s, c) => s + parseInt(c.count), 0)}
          sub="in pipeline"
          color="emerald"
          to="/recruitment"
        />
      </div>

      {/* Lead Admin Accountability */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <AccountabilityCard
          title="Observation Completion"
          icon={EyeIcon}
          data={dashboard?.observation_accountability || []}
        />
        <AccountabilityCard
          title="1:1 Completion"
          icon={ChatBubbleLeftRightIcon}
          data={dashboard?.one_on_one_accountability || []}
        />
      </div>

      {/* Compliance Alerts */}
      {(dashboard?.compliance_alerts || []).length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
            <h2 className="text-lg font-semibold text-gray-900">Compliance Alerts</h2>
          </div>
          <div className="space-y-3">
            {dashboard.compliance_alerts.slice(0, 8).map((alert, i) => (
              <Link key={i} to={alert.user_id ? `/staff/${alert.user_id}` : '/compliance?status=alerts'}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 hover:bg-orange-50/40 -mx-2 px-2 rounded-lg transition">
                <div>
                  <p className="text-sm font-medium text-gray-900">{alert.type_name || alert.name}</p>
                  <p className="text-xs text-gray-500">{alert.staff_name}</p>
                </div>
                <div className="text-right">
                  <StatusBadge status={alert.status} />
                  {alert.expiry_date && (
                    <p className="text-xs text-gray-500 mt-1">
                      Expires: {formatDateOnlyNumeric(alert.expiry_date)}
                    </p>
                  )}
                </div>
              </Link>
            ))}
          </div>
          <Link to="/compliance" className="block text-sm text-orange-500 hover:text-orange-600 font-medium mt-4">
            View all compliance &rarr;
          </Link>
        </div>
      )}

      {/* Overdue Tasks */}
      {(dashboard?.overdue_tasks || []).length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Overdue Tasks</h2>
          <div className="space-y-3">
            {dashboard.overdue_tasks.slice(0, 5).map((task) => (
              <Link key={task.id} to={`/tasks?id=${task.id}`}
                className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0 hover:bg-orange-50/40 -mx-2 px-2 rounded-lg transition">
                <div>
                  <p className="text-sm font-medium text-gray-900">{task.title}</p>
                  <p className="text-xs text-gray-500">{task.assigned_to_name}</p>
                </div>
                <div className="text-right">
                  <StatusBadge status={task.priority} />
                  <p className="text-xs text-red-500 mt-1">
                    Due: {new Date(task.due_date).toLocaleDateString()}
                  </p>
                </div>
              </Link>
            ))}
          </div>
          <Link to="/tasks" className="block text-sm text-orange-500 hover:text-orange-600 font-medium mt-4">
            View all tasks &rarr;
          </Link>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ label, value, sub, color, to }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  };

  const body = (
    <>
      <p className="text-sm font-medium opacity-75">{label}</p>
      <p className="text-3xl font-bold mt-1">{value}</p>
      <p className="text-xs opacity-60 mt-1">{sub}</p>
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        className={`rounded-2xl border p-5 block hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer ${colors[color]}`}
      >
        {body}
      </Link>
    );
  }
  return <div className={`rounded-2xl border p-5 ${colors[color]}`}>{body}</div>;
}

function AccountabilityCard({ title, icon: Icon, data }) {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-5 h-5 text-orange-500" />
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
      </div>
      {data.length === 0 ? (
        <p className="text-sm text-gray-500">No data yet</p>
      ) : (
        <div className="space-y-3">
          {data.map((row, i) => {
            const rate = row.total > 0 ? Math.round((row.completed / row.total) * 100) : 0;
            const Wrapper = row.lead_admin_id ? Link : 'div';
            const wrapperProps = row.lead_admin_id
              ? { to: `/staff/${row.lead_admin_id}`, className: 'block hover:bg-orange-50/40 -mx-2 px-2 py-1 rounded-lg transition' }
              : {};
            return (
              <Wrapper key={i} {...wrapperProps}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-gray-700">{row.lead_admin_name}</span>
                  <span className="text-sm text-gray-500">
                    {row.completed}/{row.total} ({rate}%)
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${
                      rate === 100 ? 'bg-emerald-500' : rate >= 75 ? 'bg-amber-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${rate}%` }}
                  />
                </div>
                {row.overdue > 0 && (
                  <p className="text-xs text-red-500 mt-0.5">{row.overdue} overdue</p>
                )}
              </Wrapper>
            );
          })}
        </div>
      )}
    </div>
  );
}

function QuickLink({ to, icon: Icon, label, desc }) {
  return (
    <Link
      to={to}
      className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-orange-200 transition-all flex items-start gap-4"
    >
      <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-5 h-5 text-orange-500" />
      </div>
      <div>
        <h3 className="font-semibold text-gray-900">{label}</h3>
        <p className="text-sm text-gray-500">{desc}</p>
      </div>
    </Link>
  );
}
